import {
  AppstoreOutlined,
  AudioOutlined,
  AuditOutlined,
  FileSearchOutlined,
  BankOutlined,
  BugOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DollarOutlined,
  ExperimentOutlined,
  FileDoneOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  FlagOutlined,
  GiftOutlined,
  GoldOutlined,
  IdcardOutlined,
  KeyOutlined,
  LineChartOutlined,
  NotificationOutlined,
  PhoneOutlined,
  WifiOutlined,
  RollbackOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  SmileOutlined,
  SolutionOutlined,
  SoundOutlined,
  StopOutlined,
  TagOutlined,
  TagsOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  VideoCameraOutlined,
  WalletOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';

/** Queue sizes from `GET /admin/console/attention`; null = no access. */
export interface AttentionCounts {
  pendingListeners: number | null;
  kycPending: number | null;
  voicePending: number | null;
  payoutsPending: number | null;
  supportOpen: number | null;
  moderationPending: number | null;
  paidNotCredited: number | null;
  incidentsOpen: number | null;
  /** Active call-delivery alerts (rings not reaching phones, push failures…). */
  deliveryAlerts?: number | null;
}

export interface NavItem {
  /** Unique menu key — the target, including any query string. */
  key: string;
  to: string;
  label: string;
  icon: ReactNode;
  /**
   * The permission that opens this page — the SAME key the route guard and the
   * API check. Hiding the item is presentation only: the route renders a 403
   * without it and every API behind the page refuses the call.
   */
  permission: string;
  /** Which attention count is this item's badge. */
  badge?: keyof AttentionCounts;
  /** Shown only to an agency login (role `agency`). */
  agencyOnly?: boolean;
}

/**
 * Item permission for a SUPER-ADMIN-ONLY page (the API's `@SuperAdminOnly`).
 * It is not in the permission catalogue, so no admin can be granted it and
 * only a super admin — whose `can()` is true for every key — sees the item.
 */
export const SUPER_ADMIN_ONLY = 'super_admin_only';

/** A sidebar section. Hidden entirely when none of its items are visible. */
export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

/**
 * The sidebar. Grouped by the JOB an admin is doing: act on a queue, look
 * someone up, watch sessions, follow money, grow, edit content, configure,
 * operate the system. Mirrors RBAC_CATALOG in the API for permissions; kept
 * local because the sidebar must render for every operator.
 */
export const NAV_GROUPS: NavGroup[] = [
  // An agency login's whole sidebar: its OWN account, one page per job. Hidden
  // from everyone else — a super admin holds every permission but owns no
  // agency (see visibleNav). Every page calls /admin/agency-portal/*, which
  // the API scopes to the signed-in agency.
  {
    key: 'my-agency',
    label: 'My agency',
    items: [
      { key: '/agency-portal', to: '/agency-portal', label: 'Dashboard', icon: <DashboardOutlined />, permission: 'agency_portal.view', agencyOnly: true },
      { key: '/agency-portal/listeners', to: '/agency-portal/listeners', label: 'Listeners', icon: <SolutionOutlined />, permission: 'agency_portal.view', agencyOnly: true },
      { key: '/agency-portal/users', to: '/agency-portal/users', label: 'Users', icon: <UserOutlined />, permission: 'agency_portal.view', agencyOnly: true },
      { key: '/agency-portal/support', to: '/agency-portal/support', label: 'Support', icon: <CustomerServiceOutlined />, permission: 'agency_portal.support', agencyOnly: true },
      { key: '/agency-portal/recharges', to: '/agency-portal/recharges', label: 'Recharges', icon: <RollbackOutlined />, permission: 'agency_portal.view', agencyOnly: true },
      { key: '/agency-portal/commission', to: '/agency-portal/commission', label: 'Commission', icon: <DollarOutlined />, permission: 'agency_portal.view', agencyOnly: true },
      { key: '/agency-portal/payouts', to: '/agency-portal/payouts', label: 'Payouts', icon: <WalletOutlined />, permission: 'agency_portal.view', agencyOnly: true },
    ],
  },
  {
    key: 'overview',
    label: 'Overview',
    items: [
      { key: '/', to: '/', label: 'Dashboard', icon: <DashboardOutlined />, permission: 'dashboard.view' },
      { key: '/analytics', to: '/analytics', label: 'Analytics', icon: <LineChartOutlined />, permission: 'analytics.view' },
      {
        key: '/analytics/normal-users',
        to: '/analytics/normal-users',
        label: 'Normal User Analytics',
        icon: <UsergroupAddOutlined />,
        permission: 'analytics.app_usage.view',
      },
      { key: '/reports', to: '/reports', label: 'Reports', icon: <FileTextOutlined />, permission: 'reports.view' },
      { key: '/reports/full', to: '/reports/full', label: 'Full data report', icon: <FileExcelOutlined />, permission: 'reports.full_export' },
    ],
  },
  {
    key: 'queues',
    label: 'Needs action',
    items: [
      { key: '/support', to: '/support', label: 'Support', icon: <CustomerServiceOutlined />, permission: 'support.view', badge: 'supportOpen' },
      { key: '/listeners?status=pending', to: '/listeners?status=pending', label: 'Listener applications', icon: <SolutionOutlined />, permission: 'listeners.view', badge: 'pendingListeners' },
      { key: '/kyc', to: '/kyc', label: 'ID verification', icon: <FileDoneOutlined />, permission: 'kyc.view', badge: 'kycPending' },
      { key: '/voice-verification', to: '/voice-verification', label: 'Voice verification', icon: <AudioOutlined />, permission: 'voice.view', badge: 'voicePending' },
      { key: '/moderation', to: '/moderation', label: 'Safety reports', icon: <FlagOutlined />, permission: 'moderation.view', badge: 'moderationPending' },
      { key: '/payouts', to: '/payouts', label: 'Payouts', icon: <BankOutlined />, permission: 'payouts.view', badge: 'payoutsPending' },
    ],
  },
  {
    key: 'people',
    label: 'People',
    items: [
      { key: '/users', to: '/users', label: 'Users', icon: <UserOutlined />, permission: 'users.view' },
      { key: '/listeners', to: '/listeners', label: 'Listeners', icon: <TeamOutlined />, permission: 'listeners.view' },
      { key: '/employment', to: '/employment', label: 'Employees', icon: <IdcardOutlined />, permission: 'listeners.employment.view' },
      { key: '/blocks', to: '/blocks', label: 'User blocks', icon: <StopOutlined />, permission: 'blocks.view' },
    ],
  },
  {
    key: 'sessions',
    label: 'Calls & sessions',
    items: [
      { key: '/calls', to: '/calls', label: 'Calls', icon: <PhoneOutlined />, permission: 'calls.view' },
      // Are rings reaching listeners' phones? Badge = active delivery alerts.
      { key: '/calls/delivery', to: '/calls/delivery', label: 'Call delivery', icon: <WifiOutlined />, permission: 'calls.view', badge: 'deliveryAlerts' },
      // Live and Party are the entertainment surfaces, and they sit next to each
      // other because an operator moving between them is doing the same job.
      { key: '/live', to: '/live', label: 'Live streams', icon: <VideoCameraOutlined />, permission: 'live.view' },
      { key: '/party', to: '/party', label: 'Parties', icon: <UsergroupAddOutlined />, permission: 'party.view' },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    items: [
      { key: '/refunds', to: '/refunds', label: 'Recharges', icon: <RollbackOutlined />, permission: 'payments.view', badge: 'paidNotCredited' },
      { key: '/payouts?tab=history', to: '/payouts?tab=history', label: 'Listener payouts', icon: <WalletOutlined />, permission: 'payouts.view' },
      { key: '/caller-coins', to: '/caller-coins', label: 'Coins spent per listener', icon: <DollarOutlined />, permission: 'listeners.earnings.view' },
      { key: '/temp-tracking', to: '/temp-tracking', label: 'Online-time compensation', icon: <ClockCircleOutlined />, permission: 'listeners.activity.view' },
      { key: '/listener-bonus', to: '/listener-bonus', label: 'Time bonus', icon: <FieldTimeOutlined />, permission: 'time_bonus.view' },
    ],
  },
  {
    key: 'agencies',
    label: 'Agencies',
    items: [
      { key: '/agencies', to: '/agencies', label: 'Agencies', icon: <ShopOutlined />, permission: 'agencies.view' },
      { key: '/agency-payouts', to: '/agency-payouts', label: 'Agency payouts', icon: <BankOutlined />, permission: 'agency_payouts.view' },
    ],
  },
  {
    key: 'growth',
    label: 'Growth',
    items: [
      { key: '/offers', to: '/offers', label: 'Offers', icon: <TagsOutlined />, permission: 'offers.view' },
      { key: '/promotions', to: '/promotions', label: 'Promotions', icon: <NotificationOutlined />, permission: 'promos.view' },
      { key: '/push', to: '/push', label: 'Push notifications', icon: <SendOutlined />, permission: SUPER_ADMIN_ONLY },
      { key: '/referral', to: '/referral', label: 'Referrals', icon: <UsergroupAddOutlined />, permission: 'referral.view' },
      { key: '/rewards', to: '/rewards', label: 'Reward Center', icon: <TrophyOutlined />, permission: 'rewards.view' },
      { key: '/coupons', to: '/coupons', label: 'Coupons', icon: <TagOutlined />, permission: 'coupons.view' },
      { key: '/gifts', to: '/gifts', label: 'Gifts', icon: <GiftOutlined />, permission: 'gifts.view' },
    ],
  },
  {
    key: 'content',
    label: 'Content',
    items: [
      { key: '/welcome-rules', to: '/welcome-rules', label: 'Welcome rules', icon: <SmileOutlined />, permission: 'welcome_rules.view' },
      { key: '/cms', to: '/cms', label: 'Topics & languages', icon: <AppstoreOutlined />, permission: 'cms.view' },
    ],
  },
  {
    key: 'configuration',
    label: 'Settings',
    items: [
      { key: '/call-pricing', to: '/call-pricing', label: 'Call pricing', icon: <DollarOutlined />, permission: 'call_pricing.view' },
      { key: '/coin-packages', to: '/coin-packages', label: 'Coin packages', icon: <GoldOutlined />, permission: 'coin_packages.view' },
      { key: '/ringtones', to: '/ringtones', label: 'Ringtones', icon: <SoundOutlined />, permission: 'ringtones.view' },
      { key: '/feature-flags', to: '/feature-flags', label: 'Feature switches', icon: <ExperimentOutlined />, permission: 'feature_flags.view' },
      { key: '/credentials', to: '/credentials', label: 'Payment & service keys', icon: <KeyOutlined />, permission: 'credentials.view' },
    ],
  },
  {
    key: 'system',
    label: 'System',
    items: [
      { key: '/incidents', to: '/incidents', label: 'App problems', icon: <BugOutlined />, permission: 'incidents.view', badge: 'incidentsOpen' },
      { key: '/logs', to: '/logs', label: 'Logs / Activity', icon: <FileSearchOutlined />, permission: 'logs.view' },
      { key: '/audit', to: '/audit', label: 'Admin activity', icon: <AuditOutlined />, permission: 'audit.view' },
      { key: '/admins', to: '/admins', label: 'Admin team', icon: <SafetyCertificateOutlined />, permission: 'operators.view' },
    ],
  },
];

/**
 * Groups + items this admin may see. An agency login sees ONLY its own agency
 * pages — never a Dude-team page, even one it was somehow granted — and nobody
 * else sees the agency pages.
 */
export function visibleNav(can: (permission: string) => boolean, role: string | null = null): NavGroup[] {
  const agency = role === 'agency';
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => can(i.permission) && !!i.agencyOnly === agency),
  })).filter((g) => g.items.length > 0);
}

/**
 * The menu key for a location. An item whose target carries a query string
 * (Listener applications, Listener payouts) wins when the URL has that same
 * param; otherwise the plain path item is selected.
 */
export function selectedNavKey(pathname: string, search: string, groups: NavGroup[] = NAV_GROUPS): string {
  const params = new URLSearchParams(search);
  const items = groups.flatMap((g) => g.items);
  const withQuery = items.find((i) => {
    const [path, query] = i.key.split('?');
    if (!query || path !== pathname) return false;
    return [...new URLSearchParams(query)].every(([k, v]) => params.get(k) === v);
  });
  if (withQuery) return withQuery.key;
  // Detail pages (/listeners/:id) highlight their list.
  const base = '/' + (pathname.split('/')[1] ?? '');
  return items.find((i) => i.key === pathname)?.key ?? items.find((i) => i.key === base)?.key ?? pathname;
}

/** The group that holds a menu key (to open it). */
export function groupOf(key: string, groups: NavGroup[] = NAV_GROUPS): string | undefined {
  return groups.find((g) => g.items.some((i) => i.key === key))?.key;
}

const OPEN_GROUPS_KEY = 'admin.nav.openGroups';

/** Sidebar groups the admin left open, remembered across reloads. */
export function loadOpenGroups(): string[] | null {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.every((k) => typeof k === 'string') ? parsed : null;
  } catch {
    return null;
  }
}

export function saveOpenGroups(keys: string[]): void {
  try {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(keys));
  } catch {
    // Private mode / storage full: the groups just are not remembered.
  }
}
